import React, { useCallback } from 'react';
import { Contact } from 'sr-types/lib/production/v1/graphql';
import type { Account, ActorInfo } from 'sr-types/lib/search/v1/graphql';
import type { AutocompleteMatchField } from '../../../../../common/components/AutocompleteController';
import { autoCreateQuery, client } from '../../../../../common/components/person/person';
import { Filter } from '../../../../../common/components/types';
import { EMAIL_TYPE_STRING } from '../../../../../common/constants/constants';
import { ENTITY_CONSTANTS } from '../../../../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS } from '../../../../../common/form/formConstants';
import { entityToOption } from '../../../../../common/form/forms';
import FormWidget from '../../../../../common/form/FormWidget';
import { EMAIL_FORMAT_REGEX } from '../../../../../common/form/Validator';
import { useMutationWithContext } from '../../../../../common/hooks/useMutationWithContext';
import I18n from '../../../../../common/i18n/I18n';
import { failedToastMessage } from '../../../../../common/utils/commonUtils';
import { type AddOptionValue, getPersonOption, quickAddPerson } from '../../../../../person/helpers/QuickAddPerson';
import { personDirectoryFiltersReferenceAutocomplete } from '../../../../../person/person';
import PersonOptionRenderer, {
    type PersonOptionRendererProps
} from '../../../../../shortlists/form/PersonOptionRenderer';

export interface ContactFormWidgetProps {
    name: string;
    /** Translation token for the field's label. */
    labelToken?: string;
    disabled?: boolean;
    /** Shows the current contacts without letting them be changed. */
    readOnly?: boolean;
    filter?: Filter;
    entity?: typeof ENTITY_CONSTANTS.person | typeof ENTITY_CONSTANTS.account;
    showEmail?: boolean;
    /** Fields used to match returned options, including client-side filtering. */
    matchFields?: AutocompleteMatchField[];
    addOption?: boolean;
    /**
     * Whether adding a contact the search did not find creates a Person record for them.
     *
     * Off, the added contact is carried by the email address that was typed in and nothing is
     * written to the person directory - for a field that only needs somewhere to send a
     * notification, like Deal Closure's Notify. Only a valid email address can be added then,
     * since the address is the whole of what the contact is.
     */
    autoCreatePerson?: boolean;
    required?: boolean;
    /** Shows every chip instead of collapsing the extras into "+N". */
    showAllTags?: boolean;
    value?: Contact[] | ActorInfo[] | Account[];
    onChange?: (name: string, newValue: Contact[] | ActorInfo[] | Account[]) => void;
}

/** MUI's `limitTags` value that disables the "+N" collapse. */
const SHOW_ALL_TAGS = -1;

/**
 * The contact an "Add ..." selection turns into while `autoCreatePerson` is off: the typed email
 * address, in the `contactInfo.email` shape a `ContactInput` declares, with no reference behind it.
 */
const toEmailOnlyContactOption = (address: string) => ({
    id: '',
    label: address,
    name: address,
    email: address,
    contactInfo: {
        email: { address, typeString: EMAIL_TYPE_STRING.business, verified: false },
        phone: null
    }
});

export default function ContactFormWidget({
    name,
    labelToken = 'form.contact',
    disabled = false,
    readOnly = false,
    filter = personDirectoryFiltersReferenceAutocomplete,
    entity = ENTITY_CONSTANTS.person,
    showEmail = false,
    matchFields = [],
    addOption = true,
    autoCreatePerson = true,
    required = false,
    showAllTags = false,
    value,
    onChange
}: ContactFormWidgetProps) {
    const [savePerson] = useMutationWithContext(autoCreateQuery, client);
    const isControlled = typeof onChange !== 'undefined';

    const handleCreate = useCallback(
        async (newValue: AddOptionValue, saveMutation: typeof savePerson) => {
            if (autoCreatePerson) {
                return quickAddPerson(newValue, saveMutation, true);
            }

            const address = newValue?.label?.trim() ?? '';

            if (!EMAIL_FORMAT_REGEX.test(address)) {
                failedToastMessage('validation.email.invalid');
                throw new Error(`"${address}" is not an email address, so it cannot be added.`);
            }

            return toEmailOnlyContactOption(address);
        },
        [autoCreatePerson]
    );

    return (
        <FormWidget
            component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
            entity={entity}
            name={name}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            {...(showAllTags ? { limitTags: SHOW_ALL_TAGS } : {})}
            label={<I18n token={labelToken} />}
            filter={filter}
            addOption={addOption}
            matchFields={matchFields}
            {...(isControlled ? { value: value ?? [], onChange } : {})}
            notOnlyRef
            OptionRenderer={showEmail ? OptionRendererWithEmail : PersonOptionRenderer}
            getOption={entity === ENTITY_CONSTANTS.account ? entityToOption : getPersonOption}
            onCreateHandle={handleCreate}
            saveMutation={savePerson}
            preload={false}
            multiple
            hideHelperText
        />
    );
}

const OptionRendererWithEmail = (props: PersonOptionRendererProps) => {
    return <PersonOptionRenderer {...props} showEmail />;
};
